import { useEffect, useState } from "react";

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  postCrimeCode,
  patchCrimeCode,
  getOrgConfigPortal,
  getOrgConfigPublic,
  CrimeCodeModel,
  SurveyConfiguration,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

const JURISDICTIONS = ["State", "County", "Federal", "Provincial", "Municipal"];

interface CrimeCodeEditDialogProps {
  open: boolean;
  onClose: () => void;
  crimeCode: CrimeCodeModel | null;
  orgId: string;
}

const CrimeCodeEditDialog: React.FC<CrimeCodeEditDialogProps> = ({
  open,
  onClose,
  crimeCode,
  orgId,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rmsCode, setRmsCode] = useState("");
  const [rmsCodeExtension, setRmsCodeExtension] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [surveyClasses, setSurveyClasses] = useState<string[]>([]);
  const [availableSurveyClasses, setAvailableSurveyClasses] = useState<
    { value: string; label: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [exportsToTyler, setExportsToTyler] = useState(false);

  useEffect(() => {
    if (open && orgId) {
      fetchAvailableSurveys();
      getOrgConfigPortal({ path: { org_id: orgId } })
        .then((response) =>
          setExportsToTyler(
            response.data?.metadata.rms_export_model === "tyler",
          ),
        )
        .catch(() => setExportsToTyler(false));
    }
  }, [open, orgId]);

  useEffect(() => {
    if (crimeCode) {
      setTitle(crimeCode.title);
      setDescription(crimeCode.description || "");
      setRmsCode(crimeCode.rms_code);
      setRmsCodeExtension(crimeCode.rms_code_extension || "");
      setJurisdiction(crimeCode.jurisdiction || "");
      // Handle both array and object formats
      const classes = crimeCode.visible_on_survey_classes;
      if (Array.isArray(classes)) {
        setSurveyClasses(classes);
      } else if (classes && typeof classes === "object") {
        setSurveyClasses(Object.keys(classes));
      } else {
        setSurveyClasses([]);
      }
    } else {
      setTitle("");
      setDescription("");
      setRmsCode("");
      setRmsCodeExtension("");
      setJurisdiction("");
      setSurveyClasses([]);
    }
  }, [crimeCode, open]);

  const fetchAvailableSurveys = async () => {
    if (!orgId) return;
    try {
      setLoadingSurveys(true);
      const response = await getOrgConfigPublic({
        path: { org_id: orgId },
      });
      const surveys = (response.data?.surveys || []) as SurveyConfiguration[];
      // Filter to only published, non-registry surveys
      const reportSurveys = surveys.filter(
        (survey) => !survey.is_registry && survey.survey_class,
      );
      // Create options with survey_class as value and name as label
      const options = reportSurveys.map((survey) => ({
        value: survey.survey_class!,
        label: survey.name,
      }));
      // Remove duplicates based on survey_class
      const uniqueOptions = Array.from(
        new Map(options.map((opt) => [opt.value, opt])).values(),
      );
      setAvailableSurveyClasses(uniqueOptions);
    } catch (error) {
      console.error("Error fetching available surveys:", error);
      toast.error("Failed to load report types");
    } finally {
      setLoadingSurveys(false);
    }
  };

  const handleSave = async () => {
    if (!title || !rmsCode) {
      toast.error("Title and RMS Code are required");
      return;
    }

    try {
      setLoading(true);
      if (crimeCode) {
        await patchCrimeCode({
          path: { org_id: orgId, crime_code_id: crimeCode.id },
          body: {
            title,
            description: description || undefined,
            rms_code: rmsCode,
            rms_code_extension: rmsCodeExtension || undefined,
            jurisdiction,
            visible_on_survey_classes:
              surveyClasses.length > 0 ? surveyClasses : undefined,
          },
        });
        toast.success("Crime code updated");
      } else {
        await postCrimeCode({
          path: { org_id: orgId },
          body: {
            title,
            description: description || undefined,
            rms_code: rmsCode,
            rms_code_extension: rmsCodeExtension || undefined,
            jurisdiction: jurisdiction || undefined,
            visible_on_survey_classes:
              surveyClasses.length > 0 ? surveyClasses : undefined,
          },
        });
        toast.success("Crime code created");
      }
      onClose();
    } catch (error) {
      console.error("Error saving crime code:", error);
      toast.error("Failed to save crime code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {crimeCode ? "Edit Crime Code" : "Add Crime Code"}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="RMS Code"
          value={rmsCode}
          onChange={(e) => setRmsCode(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="RMS Code Extension"
          value={rmsCodeExtension}
          onChange={(e) => setRmsCodeExtension(e.target.value)}
          margin="normal"
          helperText="Optional extension code to append to the RMS code"
        />
        <TextField
          select
          fullWidth
          label="Jurisdiction"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          margin="normal"
          helperText={
            exportsToTyler ? "Tyler exports send State when empty" : undefined
          }
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {JURISDICTIONS.map((j) => (
            <MenuItem key={j} value={j}>
              {j}
            </MenuItem>
          ))}
          {jurisdiction && !JURISDICTIONS.includes(jurisdiction) && (
            <MenuItem value={jurisdiction}>{jurisdiction}</MenuItem>
          )}
        </TextField>
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          rows={3}
        />
        <Autocomplete
          multiple
          options={availableSurveyClasses}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={surveyClasses
            .map((sc) => availableSurveyClasses.find((opt) => opt.value === sc))
            .filter((opt): opt is { value: string; label: string } => !!opt)}
          onChange={(_event, newValue) => {
            const classes = newValue.map((opt) => opt.value);
            setSurveyClasses(classes);
          }}
          loading={loadingSurveys}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Report Types (leave empty for all reports)"
              placeholder="Select report types"
              margin="normal"
              helperText="Leave empty to make this code visible on all report types"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrimeCodeEditDialog;
